import { Alert, App, Button, Descriptions, Modal, Typography } from 'antd';
import React from 'react';
import { useBusiness } from '@/prototype/context';
import type { ActionPreview as Preview } from '@/prototype/contracts';
import { contextKey } from '@/prototype/contracts';
import type { Command } from '@/prototype/service';
import { ensureValidAccessToken, hasOAuth2Session } from '@/services/oauth2';
export default function ActionPreview({
  preview,
  onClose,
  onConfirmed,
  children,
  buildInput,
}: {
  preview: Preview | null;
  onClose: () => void;
  onConfirmed?: () => void;
  children?: React.ReactNode;
  buildInput?: () => Promise<Partial<Command>>;
}) {
  const { service, state } = useBusiness();
  const { message } = App.useApp();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const inFlight = React.useRef(false);
  React.useEffect(() => setError(''), [preview?.expiresAt]);
  const [, refresh] = React.useState(0);
  React.useEffect(() => {
    if (!preview) return;
    const timer = setInterval(() => refresh((x) => x + 1), 1000);
    return () => clearInterval(timer);
  }, [preview]);
  const source = service?.get(preview?.recommendation.sourceUuid);
  const valid =
    !!preview &&
    !!source &&
    !!state &&
    source.revision === preview.sourceVersions[source.uuid] &&
    contextKey(state.context) === preview.contextKey &&
    Date.now() < Date.parse(preview.expiresAt);
  const confirm = async () => {
    if (!service || !preview || !valid || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      if (!hasOAuth2Session()) throw new Error('真实会话不可用，请重新登录');
      await ensureValidAccessToken();
      const input = buildInput ? await buildInput() : {};
      await service.confirm(preview, input);
      message.success('行动已确认');
      onClose();
      onConfirmed?.();
    } catch (e) {
      const detail =
        e instanceof Error ? e.message : '请检查必填信息，操作尚未保存';
      setError(detail);
      message.error(detail);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return (
    <Modal
      title="确认行动预览"
      open={!!preview}
      onCancel={onClose}
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            type="primary"
            disabled={!valid}
            loading={busy}
            onClick={() => void confirm()}
          >
            确认执行
          </Button>
        </>
      }
      destroyOnHidden
    >
      {valid ? (
        <>
          <Alert
            type="info"
            showIcon
            title={preview?.recommendation.label}
            description={preview?.impact}
          />
          <Descriptions
            column={1}
            style={{ marginTop: 16 }}
            items={[
              { key: 'source', label: '来源', children: source?.title },
              {
                key: 'version',
                label: '依据版本',
                children: `v${source?.revision}`,
              },
              {
                key: 'expiry',
                label: '预览有效期',
                children: new Date(preview?.expiresAt).toLocaleTimeString(
                  'zh-CN',
                ),
              },
            ]}
          />
          {error && (
            <Alert
              type="error"
              showIcon
              title={error}
              style={{ marginBlock: 12 }}
            />
          )}
          {children}
          <Typography.Paragraph type="secondary">
            确认前重新检查权限、来源状态和版本；失败不会写入部分结果。
          </Typography.Paragraph>
        </>
      ) : (
        <Alert
          type="warning"
          showIcon
          title="来源、权限或有效期已变化，请关闭后重新预览"
        />
      )}
    </Modal>
  );
}
